import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import api from '../services/api';
import { PUBLIC_BRAND_NAME, isCoachWhiteLabelActive, mergeCoachBrand, resolveOrganizationBrandName, resolveShellBrandName, resolveShellBrandLogo, resolveNavbarBrandDisplay, getUserTrainerId, applyCoachShellTheme, applyGymShellColors } from '../utils/branding';
import { applyDocumentFavicon, applyDocumentTitle } from '../utils/documentBrand';
import { useFrontendConfig } from '../context/FrontendConfigContext';
import { getPublicBrandLogo } from '../utils/frontendConfigMerge';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { isSedeOperatorUser, isSedeFinalUser, isHoldingMarcaAdmin } from '../utils/gymRoleScope';
import AdminFrontendAppearance from './AdminFrontendAppearance';
import BuildStamp from './common/BuildStamp';
import ShellNotificationBell from './dashboard/ShellNotificationBell';
import D28dTrainerEvalGate from './d28d/D28dTrainerEvalGate';

import Calculator from './Calculator';
import AdminCalculator from './AdminCalculator';
import FoodLog from './FoodLog';
import AdminFoodsManager from './AdminFoodsManager';
import AdminUsers from './AdminUsers';
import AdminPlans from './AdminPlans';
import AdminGyms from './AdminGyms';
import AdminCompanies, { UserAssignmentsPanel } from './AdminCompanies';
import MyAccount from './MyAccount';
import Progress from './Progress';
import MiProgreso from './MiProgreso';
import SupportCenter from './SupportCenter';
import Equivalentes from './Equivalentes';
import Recipes from './Recipes';
import TrainingModule from './TrainingModule';
import AdminTrainingGallery from './AdminTrainingGallery';
import AdminTrainingPendingPanel from './training/AdminTrainingPendingPanel';
import TrainingCoachClientConfig from './training/TrainingCoachClientConfig';
import TrainingCoachPaymentPanel from './training/TrainingCoachPaymentPanel';
import TrainingCoachVigenciasPanel from './training/TrainingCoachVigenciasPanel';
import CoachProductTabs from './coach/CoachProductTabs';
import AdminD28dClassGallery from './d28d/AdminD28dClassGallery';
import AdminTrainingManager from './AdminTrainingManager';
import LiveClassesPanel from './dashboard/LiveClassesPanel';
import NutritionChat from './NutritionChat';
import AuditDashboard from './AuditDashboard';
import AdminProgramsManager from './AdminProgramsManager';

import { userRoles, isFinalUser, makeHasAnyRole, isCoachFoodOperator } from './dashboard/roles';
import { getServicesFor, isFoodOnlyCommercialUser, isCoachClientUser, isTrainingOnlyFinalUser } from './dashboard/userServices';
import { emitToast } from '../context/toast';
import { isConsumerAccessBlocked } from '../utils/subscriptionAccess';
import { hasPaidFoodAccess, hasPendingFoodAccess } from '../utils/foodAccess';
import { isFoodExternal, getFoodModulePublicUrl, isFoodLegacyView, openFoodModule, isFoodSubscribeRequired, redirectToContratarFood } from '../utils/foodModule';
import {
  isTrainingExternal,
  isTrainingLegacyMode,
  isTrainingLegacyView,
  openTrainingModule,
  openTrainingModuleView,
  consumeTrainingLaunch,
} from '../utils/trainingModule';
import {
  futbolDestForUser,
  futbolSsoSentThisSession,
  isFutbolOnlyConsumer,
  openFutbolModule,
} from '../utils/futbolModule';
import { canAccessFitLive, canManageFitLiveStudio, consumeFitLiveIntent, isFitLiveUiEnabled, isFitLiveCreatorShellUser, isTrainingShellUser, clearFitLiveIntent } from '../utils/fitLiveModule';
import FitLiveApp from '../fit-live/FitLiveApp';
import FitLiveCreatorHome from '../fit-live/FitLiveCreatorHome';
import '../fit-live/styles/fit-live-shell.css';
import MyPlanView from './dashboard/MyPlanView';
import ServicesHero from './dashboard/ServicesHero';
import UserHomeCenter from './dashboard/UserHomeCenter';
import CoachHomeCenter from './dashboard/CoachHomeCenter';
import { FoodCoachTrackingPanel } from './coach/CoachEcosystemTracking';
import SuperAdminHomeCenter from './dashboard/SuperAdminHomeCenter';
import SuperAdminFinanceDashboard from './dashboard/SuperAdminFinanceDashboard';
import GymAdminHomeCenter from './dashboard/GymAdminHomeCenter';
import HoldingHomeCenter from './dashboard/HoldingHomeCenter';
import AdminPaymentLinks from './AdminPaymentLinks';
import AdminModuleVigencias from './AdminModuleVigencias';
import FoodPlanAdminView from './dashboard/FoodPlanAdminView';
import FutbolAdminView from './dashboard/FutbolAdminView';
import D28DAdminView from './dashboard/D28DAdminView';
import TrainersAdminView from './dashboard/TrainersAdminView';
import MastersHub, { LEGACY_MASTER_REDIRECTS } from './dashboard/MastersHub';
import GymMastersHub from './dashboard/GymMastersHub';
import GymAttendancePanel from './dashboard/GymAttendancePanel';
import GymMastersPlans from './dashboard/GymMastersPlans';
import GymMastersCycles from './dashboard/GymMastersCycles';
import GymMastersPrograms from './dashboard/GymMastersPrograms';
import GymMastersRoutines from './dashboard/GymMastersRoutines';
import GymMastersTrainers from './dashboard/GymMastersTrainers';
import GymMastersChallenges from './dashboard/GymMastersChallenges';
import GymOrgManagePage from './gym/GymOrgManagePage';
import CommercialPlansHub from './dashboard/CommercialPlansHub';
import ConfigurationsHub from './dashboard/ConfigurationsHub';
import PlatformRolesCatalog from './admin/PlatformRolesCatalog';
import CommunicationCenterAdmin from './admin/CommunicationCenterAdmin';
import D28dRoutinesMaster from './admin/D28dRoutinesMaster';
import D28dTrainersAdmin from './d28d/D28dTrainersAdmin';
import D28dZoomAccountsMaster from './d28d/D28dZoomAccountsMaster';
import D28dChallengesAdmin from './d28d/D28dChallengesAdmin';
import D28dProgressDashboard from './d28d/D28dProgressDashboard';
import D28dHostDailyPanel from './d28d/D28dHostDailyPanel';
import D28dCoachTracking from './d28d/D28dCoachTracking';
import { FitLiveTrackingPanel } from '../fit-live/FitLiveAdminSurfaces';
import D28dCycleReportsAdmin from './d28d/D28dCycleReportsAdmin';
import EdithD28dAssistant from './d28d/EdithD28dAssistant';
import FaqCenterAdmin from './FaqCenterAdmin';
import CoachTrainersAdmin from './dashboard/CoachTrainersAdmin';
import CoachRoutineAssistant from './coach/CoachRoutineAssistant';
import { resolveAdminUserScope } from '../utils/adminUserScopes';
import TrainingExpertProgress from '../training-module/TrainingExpertProgress';
import SpiritualCenterShell from './spiritual/admin/SpiritualCenterShell';
import SpiritualHomeButton from './spiritual/user/SpiritualHomeButton';
import SpiritualHubView from './spiritual/user/SpiritualHubView';
import BibleReaderPanel from './spiritual/user/BibleReaderPanel';
import { canShowSpiritualForUser } from '../utils/spiritualApi';
import { setOrgInitialTab } from '../utils/organizationView';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { config: frontendConfig, brandName: publicBrandName } = useFrontendConfig();

  // === Estado del shell ====================================================
  const [chatOpen, setChatOpen] = useState(false);
  // Vista actual. 'home' siempre renderiza el hero de servicios del usuario.
  const [currentView, setCurrentView] = useState('home');
  const [pendingActivateUserId, setPendingActivateUserId] = useState(null);
  const [bootRouted, setBootRouted] = useState(false);
  const stayOnShellHomeRef = useRef(false);
  const foodSsoSentRef = useRef(false);
  const futbolSsoSentRef = useRef(false);
  // Si admin/coach entra a un servicio, abrimos su panel (food-plan, d28d, training, gym).
  const [openServicePanel, setOpenServicePanel] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [dayTotals, setDayTotals] = useState(null);
  const [myPlan, setMyPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [gymBrand, setGymBrand] = useState(null);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const [holdingCompany, setHoldingCompany] = useState(null);
  const [coachBrand, setCoachBrand] = useState(null);
  const [tenantPanelGym, setTenantPanelGym] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const initialViewParam = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('view');
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const service = params.get('service');
    const view = params.get('view') || (() => {
      try {
        const stored = sessionStorage.getItem('shell_nav_view');
        // Retorno Food (v8.246) dejaba liveclasses; el boot lo ignora. Solo ?view=liveclasses es explícito.
        if (stored === 'liveclasses' || stored === 'home') return null;
        return stored;
      } catch {
        return null;
      }
    })();
    if (view === 'myaccount') {
      setCurrentView('myaccount');
      try { sessionStorage.removeItem('shell_nav_view'); } catch { /* noop */ }
      params.delete('view');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    } else if (view === 'communication') {
      setCurrentView('communication');
      try { sessionStorage.removeItem('shell_nav_view'); } catch { /* noop */ }
      params.delete('view');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    } else if (view === 'liveclasses') {
      setCurrentView('liveclasses');
      try { sessionStorage.removeItem('shell_nav_view'); } catch { /* noop */ }
      params.delete('view');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    } else if (service === 'food-plan') {
      setOpenServicePanel('food-plan');
      setCurrentView('servicePanel');
      params.delete('service');
      params.delete('view');
      const qs = params.toString();
      const next = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      window.history.replaceState({}, '', next);
    } else if (view && isFoodLegacyView(view)) {
      setCurrentView(view);
      params.delete('view');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }, []);

  const roles = useMemo(() => userRoles(user), [user]);
  const hasAnyRole = useMemo(() => makeHasAnyRole(roles), [roles]);
  const isFinal = isFinalUser(user);
  const isSedeFinal = useMemo(
    () => isSedeFinalUser(user, gymBrand),
    [user, gymBrand],
  );
  const accessBlocked = useMemo(
    () => isFinal && isConsumerAccessBlocked(user),
    [isFinal, user],
  );
  const services = useMemo(
    () => getServicesFor(user, frontendConfig, lang),
    [user, frontendConfig, lang],
  );
  const hasFoodLicense = useMemo(
    () => services.some((s) => s.id === 'food-plan'),
    [services],
  );
  const [miProgresoTab, setMiProgresoTab] = useState('general');

  /** Asistente nutricional: solo dentro de vistas FOOD legacy embebido (no Mi Progreso ni shell general). */
  const serviceShellUx = useMemo(() => {
    const hasFoodLicense = services.some((s) => s.id === 'food-plan');
    const hasD28d = services.some((s) => s.id === 'd28d' || s.id === 'live-classes');
    const hasTraining = services.some((s) => s.id === 'training');
    const foodOnly = hasFoodLicense && !hasD28d && !hasTraining;
    const foodAssistantViews = new Set([
      'myplan', 'foodlog', 'calculator', 'equivalentes', 'recipes', 'progress',
    ]);
    const inFoodPlatformView = foodAssistantViews.has(currentView)
      || (currentView === 'servicePanel' && openServicePanel === 'food-plan' && !isFoodExternal());
    return {
      hasFoodLicense,
      hasD28d,
      hasTraining,
      foodOnly,
      showShellHelpAssistant: false,
      showNutritionChatFab: isFinal
        && hasFoodLicense
        && !isFoodExternal()
        && inFoodPlatformView,
    };
  }, [services, currentView, openServicePanel, isFinal]);

  useEffect(() => {
    if (!chatOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setChatOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatOpen]);

  useEffect(() => {
    consumeTrainingLaunch(navigate, setOpenServicePanel, setCurrentView);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    try {
      const raw = sessionStorage.getItem('registration_flash');
      if (!raw) return;
      sessionStorage.removeItem('registration_flash');
      const flash = JSON.parse(raw);
      if (flash?.message) {
        emitToast({
          type: flash.type === 'error' ? 'error' : flash.type === 'info' ? 'info' : 'success',
          title: flash.title || t('common.registration', 'Registro'),
          message: flash.message,
        });
      }
    } catch { /* noop */ }
  }, [user?.id, t]);

  const isPureCoach = useMemo(
    () => roles.includes('entrenador')
      && !roles.some((r) => [
        'super_admin', 'admin_d28d', 'admin_training', 'admin_entrenador',
        'admin_gimnasio', 'admin_marca',
      ].includes(r)),
    [roles],
  );
  const isTrainingOps = useMemo(
    () => roles.some((r) => ['admin_training', 'admin_entrenador'].includes(r))
      && !roles.some((r) => ['admin_gimnasio', 'admin_marca', 'entrenador'].includes(r)),
    [roles],
  );
  const isHoldingMarca = useMemo(
    () => isHoldingMarcaAdmin(user),
    [user],
  );
  const isGymAdmin = useMemo(
    () => !isHoldingMarca && roles.some((r) => [
      'admin_gym_virtual', 'admin_gimnasio', 'admin_gym', 'admin_sede',
    ].includes(r)),
    [roles, isHoldingMarca],
  );
  const isGymOnlyAdmin = useMemo(
    () => isGymAdmin && !hasAnyRole(['super_admin', 'admin_d28d']),
    [isGymAdmin, hasAnyRole],
  );
  const isSedeAdmin = useMemo(
    () => isGymOnlyAdmin && isSedeOperatorUser(user, gymBrand),
    [isGymOnlyAdmin, user, gymBrand],
  );
  const scopedGymId = useMemo(
    () => tenantPanelGym?.id
      || (isGymAdmin ? (user?.gym_id || user?.gymId || user?.tenant_context?.gym_id) : null),
    [tenantPanelGym, isGymAdmin, user?.gym_id, user?.gymId, user?.tenant_context?.gym_id],
  );
  const inTenantGymPanel = Boolean(tenantPanelGym?.id);
  const coachUsesShellTraining = useMemo(
    () => roles.includes('entrenador') || roles.includes('nutricionista'),
    [roles],
  );
  const hasCoachTrainingAccess = useMemo(
    () => hasAnyRole([
      'entrenador', 'nutricionista', 'admin_training', 'admin_entrenador',
      'admin_marca', 'admin_gimnasio', 'admin_gym', 'super_admin',
    ]),
    [hasAnyRole],
  );
  const isTrainingGalleryContext = useMemo(
    () => isPureCoach || isTrainingOps
      || hasAnyRole(['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador']),
    [isPureCoach, isTrainingOps, hasAnyRole],
  );
  const isHybridCoachGym = useMemo(
    () => roles.includes('entrenador')
      && hasAnyRole(['admin_gimnasio', 'admin_marca', 'admin_gym']),
    [roles, hasAnyRole],
  );
  const isPureNutritionist = useMemo(
    () => roles.includes('nutricionista')
      && !roles.includes('entrenador')
      && !roles.some((r) => [
        'super_admin', 'admin_d28d', 'admin_training', 'admin_entrenador',
        'admin_gimnasio', 'admin_marca', 'admin_gym',
      ].includes(r)),
    [roles],
  );
  const isNicolasAnchorCoach = useMemo(() => {
    const mail = String(user?.email || '').trim().toLowerCase();
    return isPureCoach && [
      'nicolasdelrio718@gmail.com',
      'nicolasdelrio@foodplan.local',
      'coach.admin@d28d.local',
    ].includes(mail);
  }, [isPureCoach, user?.email]);
  const coachHasFoodModule = useMemo(
    () => isCoachFoodOperator(user)
      || hasAnyRole(['nutricionista'])
      || Boolean(user?.module_access?.food_plan || user?.module_access?.nutrition),
    [user, hasAnyRole],
  );
  const coachUsesFoodTrainerPanel = useMemo(() => isCoachFoodOperator(user), [user]);
  const canManageD28dClassGallery = useMemo(
    () => hasAnyRole(['super_admin', 'admin_d28d', 'admin_gym_virtual', 'admin_marca', 'admin_gimnasio', 'admin_gym', 'admin_sede']),
    [hasAnyRole],
  );
  const isPlatformD28dOnly = useMemo(
    () => hasAnyRole(['admin_d28d'])
      && !hasAnyRole(['super_admin', 'admin_marca', 'admin_gimnasio', 'admin_gym_virtual']),
    [hasAnyRole],
  );

  useEffect(() => {
    if (!user?.id || bootRouted) return;
    if (initialViewParam === 'communication') {
      setCurrentView('communication');
      setBootRouted(true);
      return;
    }
    if (isPlatformD28dOnly) {
      setOpenServicePanel('d28d');
      setCurrentView('servicePanel');
      setBootRouted(true);
      return;
    }
    if (isPureCoach) {
      setOpenServicePanel('training');
      setCurrentView('home');
      setBootRouted(true);
      return;
    }
    if (isTrainingOps) {
      setOpenServicePanel('training');
      setCurrentView('admintrainers');
      setBootRouted(true);
      return;
    }
    if (isHybridCoachGym) {
      setOpenServicePanel('training');
      setCurrentView('home');
      setBootRouted(true);
      return;
    }
    if (isHoldingMarca) {
      setCurrentView('home');
      setBootRouted(true);
      return;
    }
    if (isGymOnlyAdmin && !isSedeAdmin) {
      setOpenServicePanel('d28d');
      setCurrentView('servicePanel');
      setBootRouted(true);
      return;
    }
    if (isGymAdmin) {
      setCurrentView('home');
      setBootRouted(true);
      return;
    }
    if (hasAnyRole(['super_admin'])) {
      setOpenServicePanel(null);
      setCurrentView('home');
      setBootRouted(true);
      return;
    }
    if (isFinal) {
      const params = new URLSearchParams(window.location.search);
      let boot = params.get('boot');
      const tab = params.get('tab');
      const pendingPayment = params.get('pending_payment') === '1';
      if (!boot) {
        try {
          boot = sessionStorage.getItem('post_payment_boot');
          if (boot) sessionStorage.removeItem('post_payment_boot');
        } catch { /* noop */ }
      }
      // Fit Live se suma a todo usuario final: no debe quitarle al cliente de coach su entrada directa a Training.
      const productServices = services.filter((s) => s.id !== 'fit-live');
      const trainingOnly = productServices.length === 1 && productServices[0]?.id === 'training';
      const futbolOnly = (services.length === 1 && services[0]?.id === 'futbol')
        || user?.entry_host === 'futbol';
      const foodOnly = (services.length === 1 && services[0]?.id === 'food-plan')
        || isFoodOnlyCommercialUser(user);
      const paidFood = hasPaidFoodAccess(user);
      let skipFoodBoot = false;
      let shellNavView = null;
      try {
        skipFoodBoot = sessionStorage.getItem('shell_skip_food_boot') === '1';
        if (skipFoodBoot) sessionStorage.removeItem('shell_skip_food_boot');
        shellNavView = sessionStorage.getItem('shell_nav_view');
        if (shellNavView) sessionStorage.removeItem('shell_nav_view');
      } catch { /* noop */ }
      if (skipFoodBoot || shellNavView === 'liveclasses' || shellNavView === 'home') {
        stayOnShellHomeRef.current = true;
      }
      if (pendingPayment) {
        setCurrentView('myaccount');
      } else if (foodOnly && hasPendingFoodAccess(user) && !paidFood) {
        setCurrentView('home');
      } else if ((boot === 'food' || foodOnly) && !skipFoodBoot && isFoodExternal() && (paidFood || !accessBlocked)) {
        foodSsoSentRef.current = true;
        openFoodModule('/dashboard', { user });
      } else if (shellNavView === 'liveclasses' || shellNavView === 'home') {
        // Food «Volver al ecosistema» → Inicio (no mezclar con Clases en vivo D28D).
        setCurrentView('home');
      } else if (boot === 'training' || tab === 'entrenamiento' || trainingOnly || isTrainingShellUser(user)) {
        if (tab === 'entrenamiento') {
          setCurrentView('mi-progreso');
          setMiProgresoTab('entrenamiento');
        } else {
          setCurrentView('home');
        }
      } else if (boot === 'futbol' || (futbolOnly && (() => {
        try { return sessionStorage.getItem('futbol_auto_sso') === '1'; } catch { return false; }
      })())) {
        // SSO Nest solo tras login (flag) o ?boot=futbol — no al reabrir Fit Live.
        try { sessionStorage.removeItem('futbol_auto_sso'); } catch { /* noop */ }
        if (!futbolSsoSentRef.current && !futbolSsoSentThisSession()) {
          futbolSsoSentRef.current = true;
          try { sessionStorage.setItem('futbol_sso_sent', '1'); } catch { /* noop */ }
          openFutbolModule('/dashboard', futbolDestForUser(user)).then((out) => {
            if (!out?.ok) setCurrentView('home');
          });
        } else {
          setCurrentView('home');
        }
        setMiProgresoTab('general');
      } else if (futbolOnly) {
        // Ya en shell con cuenta academia: Inicio Fit Live + «Abrir futbol.app».
        setCurrentView('home');
        setMiProgresoTab('general');
      } else if ((boot === 'food' || foodOnly) && !skipFoodBoot) {
        // Food-only pendiente sin licencia: Inicio + banner Food (no contratar/status).
        setCurrentView('home');
        setMiProgresoTab('general');
      } else if (isFitLiveCreatorShellUser(user)) {
        // Creator: Inicio = estadísticas; Studio se abre desde el CTA.
        setOpenServicePanel(null);
        setCurrentView('home');
        setMiProgresoTab('general');
      } else if (canAccessFitLive(user) && services.some((s) => s.id === 'fit-live') && !isTrainingShellUser(user)) {
        setOpenServicePanel('fit-live');
        setCurrentView('servicePanel');
        setMiProgresoTab('general');
      } else {
        setCurrentView('home');
        setMiProgresoTab(tab ? (tab === 'resumen' ? 'general' : tab) : 'general');
      }
      if (boot || tab) {
        params.delete('boot');
        params.delete('tab');
        params.delete('service');
        params.delete('pending_payment');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      }
      setBootRouted(true);
    }
  }, [user?.id, bootRouted, initialViewParam, isPureCoach, isTrainingOps, isHybridCoachGym, isHoldingMarca, isGymAdmin, isGymOnlyAdmin, isSedeAdmin, isFinal, isPlatformD28dOnly, services, accessBlocked]);

  useEffect(() => {
    if (!user?.id || !bootRouted) return;
    if (isTrainingShellUser(user)) {
      clearFitLiveIntent();
      return;
    }
    if (hasAnyRole(['super_admin'])) {
      clearFitLiveIntent();
      return;
    }
    if (!canAccessFitLive(user)) return;
    const intent = consumeFitLiveIntent();
    if (!intent) return;
    setOpenServicePanel('fit-live');
    setCurrentView('servicePanel');
  }, [user?.id, bootRouted, user]);

  // Si el primer boot corrió antes de hidratar plan/licencias Food, reintentar SSO.
  useEffect(() => {
    if (!user?.id || !isFinal || !bootRouted) return;
    if (stayOnShellHomeRef.current || foodSsoSentRef.current) return;
    if (!isFoodExternal()) return;
    const foodOnly = (services.length === 1 && services[0]?.id === 'food-plan')
      || isFoodOnlyCommercialUser(user);
    if (!foodOnly) return;
    if (hasPendingFoodAccess(user) && !hasPaidFoodAccess(user)) return;
    if (!hasPaidFoodAccess(user) && accessBlocked) return;
    foodSsoSentRef.current = true;
    openFoodModule('/dashboard', { user });
  }, [user, isFinal, bootRouted, services, accessBlocked]);

  // === Carga de datos ======================================================
  useEffect(() => {
    if (!user?.id || accessBlocked || !hasFoodLicense || isFoodExternal()) {
      setMyPlan(null);
      setDayTotals(null);
      setPlanLoading(false);
      return undefined;
    }
    let active = true;

    (async () => {
      try {
        const r = await api.get('/plan/mine');
        if (active) setMyPlan(r.data?.data || null);
      } catch {
        if (active) setMyPlan(null);
      } finally {
        if (active) setPlanLoading(false);
      }
    })();

    (async () => {
      try {
        const r = await api.get('/food-log/totals', { params: { fecha: today } });
        if (active) setDayTotals(r.data?.data || null);
      } catch { /* sin licencia FOOD o sin datos del día */ }
    })();

    return () => { active = false; };
  }, [user?.id, today, accessBlocked, hasFoodLicense]);

  useEffect(() => {
    const gymId = user?.gym_id ?? user?.gymId;
    if (!gymId) { setGymBrand(null); return undefined; }
    let active = true;
    (async () => {
      try {
        const [gymRes, brandRes] = await Promise.all([
          api.get(`/gyms/${gymId}`),
          api.get(`/gyms/${gymId}/branding`).catch(() => null),
        ]);
        if (!active) return;
        const g = gymRes.data?.data || gymRes.data || {};
        const b = brandRes?.data?.branding || {};
        setGymBrand({ ...g, ...b, nombre: g.nombre || b.nombre });
      } catch {
        if (active) setGymBrand(null);
      }
    })();
    return () => { active = false; };
  }, [user?.gym_id, user?.gymId]);

  useEffect(() => {
    if (!isHoldingMarca) {
      setHoldingCompany(null);
      return undefined;
    }
    let active = true;
    api.get('/companies/mine')
      .then((res) => {
        if (active) setHoldingCompany(res.data?.company || null);
      })
      .catch(() => {
        if (active) setHoldingCompany(null);
      });
    return () => { active = false; };
  }, [isHoldingMarca, user?.id]);

  useEffect(() => {
    const gymId = user?.gym_id ?? user?.gymId;
    if (!gymId) return undefined;
    const onBrandUpdated = (event) => {
      if (String(event.detail?.gymId) !== String(gymId)) return;
      const branding = event.detail?.branding || {};
      setGymBrand((prev) => (prev ? { ...prev, ...branding } : { ...branding, id: gymId }));
    };
    window.addEventListener('gym-brand-updated', onBrandUpdated);
    return () => window.removeEventListener('gym-brand-updated', onBrandUpdated);
  }, [user?.gym_id, user?.gymId]);

  useEffect(() => {
    const profileBrand = user?.coach_branding;
    const tid = getUserTrainerId(user);
    if (!tid) {
      setCoachBrand(profileBrand || null);
      return undefined;
    }
    if (profileBrand) setCoachBrand(profileBrand);
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/trainers/${tid}/branding`);
        if (active) setCoachBrand(r.data);
      } catch {
        if (active) setCoachBrand(profileBrand || null);
      }
    })();
    const onCoachBrandUpdated = (event) => {
      const next = event?.detail;
      if (!next || typeof next !== 'object') return;
      if (tid && next.id != null && Number(next.id) !== Number(tid)) return;
      setCoachBrand((prev) => ({ ...(prev || {}), ...next }));
    };
    window.addEventListener('coach-brand-updated', onCoachBrandUpdated);
    return () => {
      active = false;
      window.removeEventListener('coach-brand-updated', onCoachBrandUpdated);
    };
  }, [user?.trainer_id, user?.trainerId, user?.coach_branding]);

  const effectiveCoachBrand = useMemo(
    () => mergeCoachBrand(user, coachBrand),
    [user, coachBrand],
  );
  const coachWhiteLabelActive = useMemo(
    () => isCoachWhiteLabelActive(user, effectiveCoachBrand),
    [user, effectiveCoachBrand],
  );

  useEffect(() => {
    const root = document.documentElement;
    if (coachWhiteLabelActive) {
      applyCoachShellTheme(effectiveCoachBrand, { active: true });
      return () => applyCoachShellTheme(effectiveCoachBrand, { active: false });
    }
    root.removeAttribute('data-coach-wl');
    applyGymShellColors(root, {
      primary: gymBrand?.primary_color,
      secondary: gymBrand?.secondary_color,
    });
    return undefined;
  }, [
    coachWhiteLabelActive,
    effectiveCoachBrand,
    gymBrand?.primary_color,
    gymBrand?.secondary_color,
  ]);

  useEffect(() => {
    const entity = coachWhiteLabelActive ? effectiveCoachBrand : gymBrand;
    if (!entity) return undefined;
    const name = resolveOrganizationBrandName({
      entity,
      platformName: publicBrandName,
    });
    const applyTitle = coachWhiteLabelActive
      ? Boolean(name)
      : Boolean(name && name !== publicBrandName);
    return applyTitle && !/\bd28d\b/i.test(String(name || ''))
      ? applyDocumentTitle(name)
      : undefined;
  }, [
    coachWhiteLabelActive,
    effectiveCoachBrand,
    gymBrand,
    publicBrandName,
  ]);

  // === Branding (white-label) =============================================
  // Fit Live cream shell: solo plataforma / Fit Live — NUNCA coaches Training (Jabel, etc.)
  const isTrainingCoachChrome = Boolean(
    !hasAnyRole(['super_admin'])
    && (
      coachWhiteLabelActive
      || user?.trainer_id
      || hasAnyRole([
        'entrenador', 'admin_training', 'admin_entrenador', 'nutricionista',
      ])
    ),
  );
  // Incluye cuentas academia: en :3002 ven Fit Live crema, no shell negro FUTBOL.APP.
  const fitLiveShell = !isTrainingCoachChrome && (
    canAccessFitLive(user)
    || isFutbolOnlyConsumer(user)
    || user?.entry_host === 'futbol'
  );
  /** Usuario final Training: islas claras (el CSS de islas solo cubría .training-coach-view). */
  const trainingConsumerChrome = Boolean(
    coachWhiteLabelActive
    || (isFinal && (isCoachClientUser(user) || isTrainingOnlyFinalUser(services))),
  );
  const resolvedBrand = isHoldingMarca
    ? (holdingCompany?.nombre || t('holding.title', 'Holding'))
    : resolveShellBrandName({
      coachBrand: effectiveCoachBrand,
      gymBrand,
      publicBrandName,
      user,
    });
  const brandName = fitLiveShell
    ? 'ECOSISTEMA FIT'
    : (() => {
      const raw = String(resolvedBrand || '').trim();
      if (/gym\s*virtual|gimnasio\s*virtual|d28d/i.test(raw)) return 'ECOSISTEMA FIT';
      return raw || 'ECOSISTEMA FIT';
    })();
  const brandLogo = fitLiveShell
    ? '/ecosistema-fit-logo.png'
    : resolveShellBrandLogo({
      coachBrand: effectiveCoachBrand,
      gymBrand,
      publicLogo: getPublicBrandLogo(frontendConfig),
      user,
    });
  const brandLogoSrc = brandLogo ? resolveMediaUrl(brandLogo) : '';

  // Paleta propia Fit Live (sin amarillo legacy).
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!fitLiveShell) {
      root.removeAttribute('data-fit-live-shell');
      return undefined;
    }
    root.setAttribute('data-fit-live-shell', 'true');
    return () => root.removeAttribute('data-fit-live-shell');
  }, [fitLiveShell]);

  // Favicon = misma URL que el logo del navbar (fuente única).
  // brandHint: apple-touch-icon / acceso rápido usa slug del gym o coach.
  useLayoutEffect(() => {
    if (!brandLogoSrc) return undefined;
    const brandHint = coachWhiteLabelActive
      ? effectiveCoachBrand
      : (gymBrand || null);
    return applyDocumentFavicon(brandLogoSrc, brandHint);
  }, [brandLogoSrc, coachWhiteLabelActive, effectiveCoachBrand, gymBrand]);

  useEffect(() => {
    if (!fitLiveShell) return undefined;
    return applyDocumentTitle('Ecosistema Fit');
  }, [fitLiveShell]);

  useEffect(() => {
    setBrandLogoFailed(false);
  }, [brandLogoSrc]);
  const navbarBrand = resolveNavbarBrandDisplay({
    brandName,
    brandLogoSrc: brandLogoFailed ? '' : brandLogoSrc,
    coachWhiteLabelActive,
  });

  // === Handlers ============================================================
  const navigateTo = (view, tab) => {
    const MI_PROGRESO_VIEWS = new Set(['mi-progreso', 'd28d-progress', 'd28d-challenges']);
    const options = {};
    if (MI_PROGRESO_VIEWS.has(view)) {
      options.miProgresoTab = tab != null && String(tab).trim() !== ''
        ? (tab === 'resumen' ? 'general' : tab)
        : 'general';
    } else if (tab) {
      const normalized = tab === 'resumen' ? 'general' : tab;
      setMiProgresoTab(normalized);
    }
    navigate(view, options);
  };

  const isFoodTrackingAdmin = useMemo(
    () => hasAnyRole(['admin_food', 'admin_food_plan'])
      && !hasAnyRole(['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador']),
    [hasAnyRole],
  );

  const navigate = (view, options = {}) => {
    const { miProgresoTab: explicitMiProgresoTab } = options;
    const VIEW_ALIASES = {
      'd28d-progress': 'mi-progreso',
      'd28d-challenges': 'mi-progreso',
      progress: hasCoachTrainingAccess && !isFoodTrackingAdmin && openServicePanel !== 'food-plan'
        ? 'progress-training'
        : view,
    };
    const resolvedView = VIEW_ALIASES[view] || view;
    if (resolvedView === 'fit-live' || resolvedView === 'service:fit-live') {
      if (!canAccessFitLive(user)) return;
      setOpenServicePanel('fit-live');
      setCurrentView('servicePanel');
      return;
    }
    if (resolvedView === 'd28d-gyms') {
      setOpenServicePanel('d28d');
      setCurrentView('d28d-gyms');
      return;
    }
    if (accessBlocked && isFinal) {
      const allowed = new Set(['home', 'myaccount', 'support']);
      const coachClient = Boolean(getUserTrainerId(user));
      const foodViews = new Set([
        'myplan', 'foodlog', 'calculator', 'equivalentes', 'recipes', 'progress-food', 'progress',
      ]);
      if (coachClient && (foodViews.has(resolvedView) || isFoodLegacyView(resolvedView))) {
        if (hasPaidFoodAccess(user)) {
          if (isFoodExternal()) {
            openFoodModule('/dashboard', { user });
            return;
          }
        } else {
          setCurrentView('myaccount');
          return;
        }
      }
      if (!allowed.has(resolvedView)) {
        setCurrentView('home');
        return;
      }
    }
    const coachTrainingClient = isFinal && Boolean(getUserTrainerId(user));
    const finalWithoutTraining = isFinal
      && !hasAnyRole(['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador'])
      && !coachTrainingClient;
    const blockedTrainingViews = new Set([
      'training', 'training-stats', 'admintraining', 'coachai',
    ]);
    if (finalWithoutTraining && blockedTrainingViews.has(resolvedView)) {
      setCurrentView('home');
      return;
    }
    if (isFinal && resolvedView === 'training-gallery' && !coachTrainingClient) {
      setCurrentView('home');
      return;
    }
    if (resolvedView === 'home') {
      if (isPlatformD28dOnly) {
        setOpenServicePanel('d28d');
        setCurrentView('servicePanel');
        return;
      }
      if (
        !hasAnyRole(['super_admin'])
        && !isFitLiveCreatorShellUser(user)
        && canAccessFitLive(user)
        && services.some((s) => s.id === 'fit-live')
        && !isTrainingShellUser(user)
        && !coachTrainingClient
      ) {
        setOpenServicePanel('fit-live');
        setCurrentView('servicePanel');
        return;
      }
      setOpenServicePanel(null);
      setSelectedProgram(null);
      setCurrentView('home');
      return;
    }
    if (resolvedView === 'training-gallery') {
      setCurrentView('training-gallery');
      return;
    }
    if (resolvedView === 'liveclasses' && isFinal) {
      setOpenServicePanel(null);
      setCurrentView('mi-progreso');
      setMiProgresoTab('clases');
      return;
    }
    const isTrainingSeguimientoNav = (
      resolvedView === 'progress-training'
      || resolvedView === 'training-stats'
      || (
        resolvedView === 'progress'
        && (
          openServicePanel === 'training'
          || (hasCoachTrainingAccess && !isFoodTrackingAdmin && openServicePanel !== 'food-plan')
        )
      )
    );
    if (resolvedView === 'progress-food' && isFoodExternal() && !coachUsesFoodTrainerPanel) {
      openFoodModule('/dashboard');
      return;
    }
    if (isFoodExternal() && isFoodLegacyView(resolvedView) && !isTrainingSeguimientoNav && !coachUsesFoodTrainerPanel) {
      openFoodModule('/dashboard');
      return;
    }
    if (resolvedView === 'food-plan-launch') {
      openFoodModule('/dashboard');
      return;
    }
    // Coaches: galería, IA, seguimiento, etc. dentro del shell (dashboard unificado).
    if (isTrainingExternal() && isTrainingLegacyView(resolvedView) && !coachUsesShellTraining) {
      openTrainingModuleView(resolvedView, user);
      return;
    }
    setCurrentView(resolvedView);
    if (resolvedView === 'mi-progreso') {
      const nextTab = explicitMiProgresoTab !== undefined
        ? (explicitMiProgresoTab === 'resumen' ? 'general' : explicitMiProgresoTab)
        : 'general';
      setMiProgresoTab(nextTab);
      if (nextTab === 'general') {
        window.dispatchEvent(new Event('d28d:progress:refresh'));
      }
    }
    const trainingShellViews = new Set([
      'coachai', 'admintraining', 'progress', 'progress-training', 'training', 'training-stats',
    ]);
    const panelBoundViews = new Set([
      'adminusers', 'admintrainers', 'admingallery', 'd28d-gallery', 'coachai', 'admintraining',
      'progress', 'progress-training', 'training-stats', 'commercial-plans-coach',
    ]);

    if (resolvedView === 'progress-food' && isFoodTrackingAdmin) {
      setOpenServicePanel('food-plan');
    } else if (resolvedView === 'progress' && isFoodTrackingAdmin && openServicePanel !== 'training') {
      setOpenServicePanel('food-plan');
    } else if (panelBoundViews.has(resolvedView) && openServicePanel) {
      /* preserva panel activo (d28d, training, food-plan) */
    } else if (resolvedView === 'adminusers') {
      if (isPureCoach || isTrainingOps) {
        setOpenServicePanel('training');
      } else if (hasAnyRole(['admin_training', 'admin_entrenador']) && !hasAnyRole(['super_admin'])) {
        setOpenServicePanel('training');
      } else if (hasAnyRole(['admin_d28d']) && !hasAnyRole(['super_admin'])) {
        setOpenServicePanel('d28d');
      } else {
        setOpenServicePanel(null);
      }
    } else if (resolvedView === 'admintrainers') {
      if (isTrainingOps || (hasAnyRole(['admin_training', 'admin_entrenador']) && !hasAnyRole(['super_admin']))) {
        setOpenServicePanel('training');
      } else {
        setOpenServicePanel(null);
      }
    } else if (resolvedView === 'admingallery') {
      if (isTrainingGalleryContext) {
        setOpenServicePanel('training');
      } else if (!openServicePanel) {
        setOpenServicePanel(null);
      }
    } else if (resolvedView === 'd28d-gallery') {
      if (canManageD28dClassGallery) {
        if (openServicePanel === 'd28d') {
          /* preserva panel D28D */
        } else if (hasAnyRole(['admin_d28d', 'super_admin']) && !isGymAdmin) {
          setOpenServicePanel('d28d');
        } else {
          setOpenServicePanel(null);
        }
      }
    } else if (['programs', 'd28dzoom'].includes(resolvedView)) {
      if (hasAnyRole(['admin_d28d', 'super_admin']) && !isGymAdmin) {
        setOpenServicePanel('d28d');
      }
    } else if (resolvedView === 'liveclasses') {
      if (hasAnyRole(['admin_d28d', 'super_admin']) && !isGymAdmin) {
        setOpenServicePanel('d28d');
      } else {
        setOpenServicePanel(null);
      }
    } else if (isTrainingLegacyView(resolvedView) || trainingShellViews.has(resolvedView)) {
      setOpenServicePanel('training');
    } else if (resolvedView === 'commercial-plans-coach') {
      setOpenServicePanel('training');
    } else if (resolvedView === 'servicePanel') {
      if (isPlatformD28dOnly && !openServicePanel) {
        setOpenServicePanel('d28d');
      }
      /* mantiene openServicePanel actual */
    } else {
      setOpenServicePanel(null);
    }
  };

  const backToTrainingPanel = () => {
    setOpenServicePanel('training');
    setCurrentView('servicePanel');
  };

  const onPickService = async (service) => {
    if (!service) return;
    const adminFoodInShell = service.id === 'food-plan'
      && !isFinal
      && service.destinationView?.startsWith('service:');
    if (
      !adminFoodInShell
      && (service.destinationView === 'external:food' || (service.moduleLaunch === 'food' && isFoodExternal()))
    ) {
      openFoodModule('/dashboard');
      return;
    }
    if (service.destinationView === 'external:training' || (service.moduleLaunch === 'training' && isTrainingExternal())) {
      if (coachUsesShellTraining) {
        setOpenServicePanel('training');
        setCurrentView('servicePanel');
        return;
      }
      const sub = isFinal ? '/athlete' : '/coach';
      await openTrainingModule('/dashboard', sub, user);
      return;
    }
    if (service.destinationView === 'external:futbol' || service.moduleLaunch === 'futbol') {
      const out = await openFutbolModule('/dashboard', futbolDestForUser(user));
      if (!out?.ok) {
        emitToast({
          type: 'error',
          title: 'futbol.app',
          message: out?.error || 'No se pudo abrir futbol.app (SSO)',
        });
      }
      return;
    }
    if (service.moduleLaunch === 'training' && isTrainingLegacyMode()) {
      if (isFinal) {
        navigate('training');
        return;
      }
      setOpenServicePanel('training');
      setCurrentView('servicePanel');
      return;
    }
    if (service.id === 'training' && isFinal) {
      if (isTrainingExternal()) {
        openTrainingModule('/dashboard', '/athlete', user);
        return;
      }
      setCurrentView('mi-progreso');
      setMiProgresoTab('entrenamiento');
      return;
    }
    if (service.id === 'live-classes') {
      if (isFinal) {
        setCurrentView('mi-progreso');
        setMiProgresoTab('clases');
        return;
      }
      setOpenServicePanel(null);
      setCurrentView('liveclasses');
      return;
    }
    if (service.id === 'fit-live') {
      if (!canAccessFitLive(user)) return;
      setOpenServicePanel('fit-live');
      setCurrentView('servicePanel');
      return;
    }
    if (service.id === 'd28d') {
      if (isFinal) {
        setCurrentView('mi-progreso');
        setMiProgresoTab('clases');
        return;
      }
    }
    if (service.destinationView?.startsWith('service:')) {
      // Admin/coach → panel del servicio (gym vive solo dentro de D28D).
      let moduleId = service.destinationView.split(':')[1];
      if (moduleId === 'gym') moduleId = 'd28d';
      setOpenServicePanel(moduleId);
      setCurrentView('servicePanel');
      return;
    }
    // Usuario final → directo a su experiencia de consumo.
    navigate(service.destinationView);
  };

  const onBackToHome = () => {
    setOpenServicePanel(null);
    setSelectedProgram(null);
    setCurrentView('home');
  };

  // === Navegación principal (clara, contextual al rol) =====================
  // Regla: la barra principal queda corta y clara. Para roles administrativos
  // que necesitan acceso rápido a todos los maestros, se renderiza una
  // BARRA RÁPIDA SECUNDARIA debajo (ver `quickAdminItems`).
  const navItems = useMemo(() => {
    const home = t('nav.home', 'Inicio');
    const account = t('nav.myaccount', 'Mi Cuenta');
    if (isFinal) {
      // Creator Fit Live: solo Inicio (stats + filtro). Studio desde el CTA del home.
      if (isFitLiveCreatorShellUser(user)) {
        return [{ id: 'home', label: home }];
      }
      const items = [
        { id: 'home', label: home },
      ];
      if (!accessBlocked) {
        items.push({ id: 'mi-progreso', label: t('nav.mi_progreso', 'Mi Progreso') });
        if (isSedeFinal) {
          items.push({ id: 'd28d-gallery', label: t('nav.gallery_classes', 'GRABACIONES DE CLASE') });
        }
        if (getUserTrainerId(user)) {
          items.push({ id: 'training-gallery', label: t('nav.gallery_exercises', 'Biblioteca ejercicios') });
        }
      }
      if (canShowSpiritualForUser(user) && !accessBlocked) {
        items.push({ id: 'spiritual-today', label: t('nav.spiritual', 'Viento Recio') });
      }
      items.push(
        { id: 'support', label: t('nav.support', 'Soporte') },
        { id: 'myaccount', label: account },
      );
      return items;
    }

    if (!hasAnyRole(['super_admin'])) {
      if (hasAnyRole(['admin_d28d']) && !hasAnyRole(['admin_marca', 'admin_gimnasio', 'admin_gym_virtual'])) {
        return [
          { id: 'servicePanel', label: t('nav.services', 'Servicios') },
          { id: 'masters', label: t('nav.masters', 'Maestros') },
          { id: 'liveclasses', label: t('nav.liveclasses', 'Clases en Vivo') },
          { id: 'adminusers', label: t('nav.users', 'Usuarios') },
          { id: 'd28dtracking', label: t('nav.tracking', 'Seguimiento') },
          { id: 'd28d-gyms', label: t('nav.gyms', 'Gimnasios') },
          { id: 'd28d-gallery', label: t('nav.gallery_classes', 'GRABACIONES DE CLASE') },
          { id: 'myaccount', label: account },
        ];
      }
      if (hasAnyRole(['admin_food', 'admin_food_plan']) && !hasAnyRole(['admin_marca', 'admin_gimnasio'])) {
        if (isFoodExternal()) {
          return [
            { id: 'home', label: home },
            { id: 'adminusers', label: t('nav.users', 'Usuarios') },
            { id: 'myaccount', label: account },
          ];
        }
        return [
          { id: 'home', label: home },
          { id: 'adminusers', label: t('nav.users', 'Usuarios') },
          { id: 'admin', label: t('nav.plans_short', 'Planes') },
          { id: 'foodsmanager', label: t('nav.foods_catalog', 'Alimentos') },
          { id: 'recipes', label: t('nav.recipes', 'Recetas') },
          { id: 'progress', label: t('nav.tracking', 'Seguimiento') },
          { id: 'myaccount', label: account },
        ];
      }
      if (hasAnyRole(['admin_entrenador', 'admin_training']) && !hasAnyRole(['admin_marca', 'admin_gimnasio'])) {
        return [
          { id: 'home', label: home },
          { id: 'adminusers', label: t('nav.users', 'Usuarios') },
          { id: 'modulevigencias', label: t('nav.vigencias', 'Vigencias') },
          { id: 'admintraining', label: t('nav.routines', 'Rutinas') },
          { id: 'admingallery', label: t('nav.gallery', 'Galería') },
          { id: 'progress', label: t('nav.tracking', 'Seguimiento') },
          { id: 'myaccount', label: account },
        ];
      }
      if ((roles.includes('entrenador_d28d') || roles.includes('entrenador_gym')) && !roles.includes('entrenador')) {
        return [
          { id: 'home', label: home },
          { id: 'd28d-host-daily', label: t('nav.host_daily', 'Mi día') },
          { id: 'liveclasses', label: t('nav.liveclasses', 'Clases en Vivo') },
        ];
      }
      if (isPureNutritionist) {
        return [
          { id: 'home', label: home },
          { id: 'adminusers', label: t('nav.myusers', 'Clientes') },
          { id: 'progress', label: t('nav.tracking', 'Seguimiento') },
          { id: 'admintraining', label: t('nav.edit_routines', 'Editar rutinas') },
          { id: 'myaccount', label: account },
        ];
      }
      if (isHybridCoachGym) {
        return [
          { id: 'home', label: home },
          { id: 'progress-training', label: t('nav.tracking_training', 'Seguimiento entreno') },
          { id: 'progress-food', label: t('nav.tracking_food', 'Seguimiento food') },
          { id: 'adminusers', label: t('nav.myusers', 'Clientes') },
          { id: 'coachai', label: t('nav.coach_ai', 'IA Rutinas') },
          { id: 'admintraining', label: t('nav.edit_routines', 'Editar rutinas') },
          { id: 'admingallery', label: t('nav.gallery_exercises', 'Biblioteca ejercicios') },
          { id: 'training-stats', label: t('panel.training.cards.training-stats.title', 'Estadísticas') },
          ...(canShowSpiritualForUser(user) ? [{ id: 'spiritual-today', label: t('nav.spiritual', 'Viento Recio') }] : []),
          { id: 'liveclasses', label: t('nav.liveclasses', 'Clases en Vivo') },
          { id: 'd28d-gallery', label: t('nav.gallery_classes', 'GRABACIONES DE CLASE') },
          { id: 'admingyms', label: t('nav.mybrand', 'Mi marca') },
          { id: 'myaccount', label: account },
        ];
      }
      if (roles.includes('entrenador') && isPureCoach) {
        const coachNav = [
          { id: 'home', label: home },
          { id: 'progress-training', label: t('nav.tracking_training', 'Seguimiento entreno') },
          ...(coachHasFoodModule ? [{ id: 'progress-food', label: t('nav.tracking_food', 'Seguimiento food') }] : []),
          { id: 'adminusers', label: t('nav.myusers', 'Clientes') },
          { id: 'coachai', label: t('nav.coach_ai', 'IA Rutinas') },
          { id: 'admintraining', label: t('nav.edit_routines', 'Editar rutinas') },
          { id: 'admingallery', label: t('nav.gallery', 'Galería') },
          { id: 'training-coach-config', label: t('nav.photos_measurements', 'Fotos y medidas') },
          { id: 'training-coach-vigencias', label: t('nav.training_vigencias', 'Vigencias Training') },
          { id: 'training-coach-pay', label: t('nav.activate_clients', 'Activar clientes') },
          { id: 'training-stats', label: t('panel.training.cards.training-stats.title', 'Estadísticas') },
          { id: 'communication', label: t('nav.communication', 'Comunicación') },
          ...(canShowSpiritualForUser(user) ? [{ id: 'spiritual-today', label: t('nav.spiritual', 'Viento Recio') }] : []),
          ...(isNicolasAnchorCoach ? [{ id: 'commercial-plans-coach', label: t('nav.commercial_plans', 'Planes comerciales') }] : []),
          { id: 'myaccount', label: account },
        ];
        return coachNav;
      }
      if (isTrainingOps) {
        return [
          { id: 'admintrainers', label: t('nav.trainers_admin', 'Entrenadores') },
          { id: 'adminusers', label: t('nav.coach_users', 'Usuarios coaches') },
          { id: 'progress', label: t('nav.tracking', 'Seguimiento') },
          { id: 'myaccount', label: account },
        ];
      }
    if (isHoldingMarca) {
      return [
        { id: 'home', label: home },
        { id: 'myaccount', label: account },
      ];
    }
    if (isGymOnlyAdmin && !isSedeAdmin) {
      const hasD28dService = services.some((s) => s.id === 'd28d' || s.id === 'live-classes');
      const items = [
        { id: 'home', label: home },
        { id: 'masters', label: t('nav.masters', 'Maestros') },
        { id: 'adminusers', label: t('nav.users', 'Usuarios') },
        { id: 'd28dtracking', label: t('nav.tracking', 'Seguimiento') },
        { id: 'myaccount', label: account },
      ];
      if (hasD28dService) {
        items.splice(1, 0, { id: 'servicePanel', label: t('nav.services', 'Servicios') });
      }
      return items;
    }
    if (hasAnyRole(['admin_gym_virtual', 'admin_gimnasio', 'admin_gym', 'admin_sede'])) {
        if (isSedeAdmin) {
          return [
            { id: 'home', label: home },
            { id: 'adminusers', label: t('nav.users', 'Usuarios') },
            { id: 'liveclasses', label: t('nav.liveclasses', 'Clases en Vivo') },
            { id: 'd28dtracking', label: t('nav.tracking', 'Seguimiento') },
            { id: 'myaccount', label: account },
          ];
        }
      }
    }

    if (hasAnyRole(['super_admin'])) {
      return [
        { id: 'home', label: home },
        { id: 'adminusers', label: t('nav.users', 'Usuarios') },
        { id: 'organizations', label: t('nav.organizations', 'Organizaciones') },
        { id: 'masters', label: t('nav.masters', 'Maestros') },
        { id: 'd28dtracking', label: t('nav.tracking', 'Seguimiento') },
        { id: 'training-pending', label: t('nav.training_pending', 'Training pendientes') },
        { id: 'myaccount', label: account },
      ];
    }
    return [
      { id: 'home', label: home },
      { id: 'myaccount', label: account },
    ];
  }, [isFinal, isSedeFinal, roles, hasAnyRole, services, lang, t, user, isHybridCoachGym, isPureCoach, isPureNutritionist, accessBlocked, isSedeAdmin, isHoldingMarca, isGymOnlyAdmin, coachHasFoodModule, isNicolasAnchorCoach]);

  // === Vistas =============================================================
  const renderHome = () => {
    if (isFinal && isFitLiveCreatorShellUser(user)) {
      return (
        <FitLiveCreatorHome
          onOpenStudio={() => {
            setOpenServicePanel('fit-live');
            setCurrentView('servicePanel');
          }}
        />
      );
    }
    if (isFinal) {
      return (
        <UserHomeCenter
          onNavigate={navigateTo}
          onOpenSpiritual={() => navigate('spiritual-today')}
          gymBrand={gymBrand}
        />
      );
    }
    if (isPureCoach) {
      return (
        <CoachHomeCenter
          onNavigate={navigate}
          onOpenTrainingPanel={backToTrainingPanel}
          onOpenSpiritual={() => navigate('spiritual-today')}
          showSpiritual={canShowSpiritualForUser(user)}
          showFoodTracking={coachHasFoodModule}
        />
      );
    }
    if (isHybridCoachGym) {
      return (
        <CoachHomeCenter
          onNavigate={navigate}
          onOpenTrainingPanel={backToTrainingPanel}
          onOpenSpiritual={() => navigate('spiritual-today')}
          showSpiritual={canShowSpiritualForUser(user)}
          variant="hybrid-gym"
          showFoodTracking
        />
      );
    }
    if (isHoldingMarca) {
      return (
        <HoldingHomeCenter onNavigate={navigate} />
      );
    }
    if (isGymAdmin) {
      return (
        <GymAdminHomeCenter
          onNavigate={navigate}
          gymBrand={gymBrand}
          scopeGymId={scopedGymId || user?.gym_id || user?.gymId}
          isSedeAdmin={isSedeAdmin}
        />
      );
    }
    if (hasAnyRole(['super_admin'])) {
      return (
        <SuperAdminHomeCenter
          onNavigate={navigate}
          onOpenServicePanel={(panel, view = 'servicePanel') => {
            setOpenServicePanel(panel);
            setCurrentView(view);
          }}
          onOpenSpiritual={() => navigate('spiritual-today')}
        />
      );
    }
    if (isPlatformD28dOnly) {
      return renderServicePanel();
    }
    if (hasAnyRole(['admin_d28d'])) {
      return (
        <ServicesHero
          user={user}
          services={services}
          brandName={brandName}
          onPickService={onPickService}
        />
      );
    }
    return (
      <div className="space-y-4">
        <ServicesHero
          user={user}
          services={services}
          brandName={brandName}
          onPickService={onPickService}
        />
        {canShowSpiritualForUser(user) ? (
          <SpiritualHomeButton onOpen={() => navigate('spiritual-today')} />
        ) : null}
      </div>
    );
  };

  const renderServicePanel = () => {
    const effectiveServicePanel = openServicePanel || (isPlatformD28dOnly ? 'd28d' : null);
    if (!effectiveServicePanel) return renderHome();
    const coachDualTabs = Boolean(
      (isPureCoach || coachUsesFoodTrainerPanel)
      && hasCoachTrainingAccess
      && (hasFoodLicense || coachUsesFoodTrainerPanel),
    );
    const wrapCoachTabs = (panelId, body) => (
      <div className="space-y-0">
        {coachDualTabs ? (
          <CoachProductTabs
            active={panelId}
            showTraining
            showFood
            onSelectTraining={() => {
              setOpenServicePanel('training');
              setCurrentView('servicePanel');
            }}
            onSelectFood={() => {
              setOpenServicePanel('food-plan');
              setCurrentView('servicePanel');
            }}
          />
        ) : null}
        {body}
      </div>
    );
    if (effectiveServicePanel === 'food-plan') {
      return wrapCoachTabs(
        'food-plan',
        <FoodPlanAdminView
          hasAnyRole={hasAnyRole}
          onNavigate={navigate}
          onBack={onBackToHome}
          foodExternal={isFoodExternal()}
          foodExternalUrl={getFoodModulePublicUrl()}
        />,
      );
    }
    if (effectiveServicePanel === 'fit-live') {
      if (!canAccessFitLive(user)) return renderHome();
      return (
        <FitLiveApp
          user={user}
          onBack={onBackToHome}
          canManageStudio={canManageFitLiveStudio(user)}
        />
      );
    }
    if (effectiveServicePanel === 'futbol') {
      if (!hasAnyRole(['super_admin'])) return renderHome();
      return (
        <FutbolAdminView
          onBack={() => {
            setOpenServicePanel(null);
            setCurrentView('masters');
          }}
        />
      );
    }
    if (effectiveServicePanel === 'd28d') {
      const navigateD28d = (view) => {
        const gymMasterViews = new Set([
          'masters', 'gym-masters-programs', 'gym-masters-cycles', 'gym-masters-plans',
          'gym-masters-routines', 'gym-masters-trainers', 'gym-masters-challenges',
        ]);
        const d28dViews = new Set([
          'liveclasses', 'programs', 'd28dzoom', 'd28d-routines', 'd28d-challenges-admin',
          'd28d-trainers', 'admingyms', 'adminusers', 'd28d-gallery', 'adminliveclasses',
          'd28d-cycle-reports', 'commercial-plans', 'configurations', 'modulevigencias',
          'edith-d28d', 'finance-d28d',
        ]);
        if (gymMasterViews.has(view)) {
          setOpenServicePanel('d28d');
          setCurrentView(view);
          return;
        }
        if (view === 'd28d-routines') {
          setOpenServicePanel('d28d-routines');
          setCurrentView('servicePanel');
          return;
        }
        if (view === 'd28d-challenges-admin') {
          setOpenServicePanel('d28d');
          setCurrentView(view);
          return;
        }
        if (d28dViews.has(view)) {
          setOpenServicePanel('d28d');
          setCurrentView(view);
          return;
        }
        navigate(view);
      };
      return (
        <D28DAdminView
          hasAnyRole={hasAnyRole}
          isGymScoped={isGymOnlyAdmin && !isSedeAdmin}
          onNavigate={navigateD28d}
          onPickProgram={(programId) => {
            setSelectedProgram(programId);
            setOpenServicePanel('d28d');
            setCurrentView('programs');
          }}
          onBack={onBackToHome}
        />
      );
    }
    if (effectiveServicePanel === 'training') {
      return wrapCoachTabs(
        'training',
        <TrainersAdminView
          hasAnyRole={hasAnyRole}
          onNavigate={navigate}
          onBack={isPureCoach || isTrainingOps ? () => navigate('home') : onBackToHome}
          trainingExternal={isTrainingExternal() && !coachUsesShellTraining}
          useShellCoachPanel={coachUsesShellTraining}
          variant={isTrainingOps ? 'ops' : 'coach'}
        />,
      );
    }
    if (effectiveServicePanel === 'd28d-zoom') {
      if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
      return (
        <D28dZoomAccountsMaster
          onBack={() => {
            setOpenServicePanel(null);
            setCurrentView('masters');
          }}
        />
      );
    }
    if (effectiveServicePanel === 'd28d-routines') {
      if (!hasAnyRole(['super_admin', 'admin_d28d', 'entrenador_d28d'])) {
        return renderHome();
      }
      return (
        <D28dRoutinesMaster
          variant="platform"
          readOnly={!hasAnyRole(['super_admin', 'admin_d28d'])}
          onBack={() => {
            setOpenServicePanel('d28d');
            setCurrentView('servicePanel');
          }}
        />
      );
    }
    if (effectiveServicePanel === 'commercial-plans'
      || ['d28d-plans', 'training-plans', 'program-invites'].includes(effectiveServicePanel)) {
      if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
      return (
        <CommercialPlansHub
          d28dOnly={hasAnyRole(['admin_d28d']) && !hasAnyRole(['super_admin'])}
          onBack={() => {
            if (hasAnyRole(['admin_d28d']) && !hasAnyRole(['super_admin'])) {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
              return;
            }
            setOpenServicePanel(null);
            setCurrentView('masters');
          }}
          onOpenVigencias={() => navigate('modulevigencias')}
        />
      );
    }
    if (effectiveServicePanel === 'configurations') {
      if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
      return (
        <ConfigurationsHub
          hasAnyRole={hasAnyRole}
          onBack={() => {
            setOpenServicePanel(null);
            setCurrentView('masters');
          }}
          onNavigate={(view) => {
            setOpenServicePanel(null);
            setCurrentView(view);
          }}
        />
      );
    }
    if (effectiveServicePanel === 'coach-routines') {
      if (!hasAnyRole(['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador'])) {
        return renderHome();
      }
      return (
        <D28dRoutinesMaster
          variant="coach"
          readOnly={false}
          onBack={onBackToHome}
        />
      );
    }
    if (effectiveServicePanel === 'modulevigencias') {
      if (!hasAnyRole(['super_admin', 'admin_d28d', 'admin_training', 'admin_entrenador', 'admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_sede', 'entrenador', 'nutricionista'])) {
        return renderHome();
      }
      return (
        <AdminModuleVigencias
          scopeIsSede={isSedeAdmin}
          initialModule={
            effectiveServicePanel === 'food-plan' || openServicePanel === 'food-plan'
              ? 'food'
              : (effectiveServicePanel === 'training' || openServicePanel === 'training' ? 'training' : 'food')
          }
          onBack={() => {
            if (hasAnyRole(['admin_d28d']) && !hasAnyRole(['super_admin'])) {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
              return;
            }
            if (isSedeAdmin) {
              navigate('home');
              return;
            }
            setOpenServicePanel(null);
            setCurrentView('masters');
          }}
        />
      );
    }
    if (effectiveServicePanel === 'live-classes') {
      return (
        <LiveClassesPanel
          user={user}
          canProgram={false}
          consumerOnly
          programId={selectedProgram}
          onBack={onBackToHome}
        />
      );
    }
    return renderHome();
  };

  // === Dispatcher principal ================================================
  const renderOrganizations = (initialTab) => {
    if (!hasAnyRole(['super_admin'])) return renderHome();
    return (
      <AdminCompanies
        initialTab={initialTab}
        onNavigate={navigate}
        onOpenGymPanel={(gym) => {
          setTenantPanelGym(gym);
          setCurrentView('virtual-gym-panel');
        }}
      />
    );
  };

  const renderContent = () => {
    switch (currentView) {
      case 'calculator': return <Calculator />;
      case 'admin': return <AdminCalculator />;
      case 'foodlog': return <FoodLog />;
      case 'recipes': return <Recipes />;
      case 'foodsmanager': return hasAnyRole(['super_admin', 'admin_food_plan', 'admin_food']) ? <AdminFoodsManager /> : null;
      case 'adminusers':
        if (isHoldingMarca) return renderHome();
        return (
          <AdminUsers
            scope={isPlatformD28dOnly
              ? 'd28d'
              : resolveAdminUserScope({
                openServicePanel,
                isCoachActor: isPureCoach,
                hasAnyRole,
              })}
            scopeGymId={(isGymOnlyAdmin || isSedeAdmin || inTenantGymPanel) ? scopedGymId : null}
            scopeGymName={tenantPanelGym?.name || ''}
            pendingActivateUserId={pendingActivateUserId}
            onPendingActivateConsumed={() => setPendingActivateUserId(null)}
          />
        );
      case 'd28d-gyms':
        if (!hasAnyRole(['admin_d28d']) || hasAnyRole(['super_admin'])) return renderHome();
        return (
          <AdminUsers
            scope="d28d"
            initialViewTab="gyms"
            scopeGymId={null}
            scopeGymName=""
            pendingActivateUserId={pendingActivateUserId}
            onPendingActivateConsumed={() => setPendingActivateUserId(null)}
          />
        );
      case 'adminplans': return hasAnyRole(['super_admin']) ? <AdminPlans /> : null;
      case 'organizations':
        return renderOrganizations();
      case 'asignaciones':
      case 'admincompanies':
        return hasAnyRole(['super_admin']) ? <UserAssignmentsPanel /> : renderHome();
      case 'admingyms':
        if (isGymAdmin || inTenantGymPanel) {
          return (
            <GymOrgManagePage
              scopeGymId={scopedGymId || user?.gym_id || user?.gymId}
              onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : isGymAdmin ? 'home' : 'masters')}
            />
          );
        }
        if (hasAnyRole(['super_admin'])) {
          return renderOrganizations('gimnasios');
        }
        return renderHome();
      case 'gym-mi-marca':
        if (!isGymAdmin && !inTenantGymPanel) return renderHome();
        return (
          <GymOrgManagePage
            scopeGymId={scopedGymId || user?.gym_id || user?.gymId}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : isGymAdmin ? 'home' : 'masters')}
          />
        );
      case 'admintenants':
        return renderOrganizations('gimnasios');
      case 'myaccount':
        if (
          hasAnyRole(['entrenador_gym', 'entrenador_d28d'])
          && !hasAnyRole(['admin_gym', 'admin_gimnasio', 'admin_marca', 'admin_d28d', 'super_admin'])
        ) {
          return renderHome();
        }
        return <MyAccount />;
      case 'audit':
        return hasAnyRole(['super_admin']) ? (
          <AuditDashboard onBack={() => navigate('masters')} />
        ) : null;
      case 'finance':
        return hasAnyRole(['super_admin']) ? (
          <SuperAdminFinanceDashboard onBack={() => navigate('home')} />
        ) : renderHome();
      case 'finance-d28d':
        return hasAnyRole(['super_admin', 'admin_d28d']) ? (
          <SuperAdminFinanceDashboard
            moduleScope="d28d"
            onBack={() => {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            }}
          />
        ) : renderHome();
      case 'finance-food':
        return hasAnyRole(['super_admin', 'admin_food', 'admin_food_plan']) ? (
          <SuperAdminFinanceDashboard
            moduleScope="food"
            onBack={() => {
              setOpenServicePanel('food-plan');
              setCurrentView('servicePanel');
            }}
            onOpenVigencias={hasAnyRole(['super_admin']) ? () => {
              setOpenServicePanel('food-plan');
              setCurrentView('modulevigencias');
            } : null}
          />
        ) : renderHome();
      case 'finance-training':
        return hasAnyRole(['super_admin', 'admin_training', 'admin_entrenador', 'entrenador', 'nutricionista']) ? (
          <SuperAdminFinanceDashboard
            moduleScope="training"
            coachScoped={
              hasAnyRole(['entrenador', 'nutricionista'])
              && !hasAnyRole(['super_admin', 'admin_training', 'admin_entrenador'])
            }
            onBack={() => {
              const coachFinanceHome = hasAnyRole(['entrenador', 'nutricionista'])
                && !hasAnyRole(['super_admin', 'admin_training', 'admin_entrenador']);
              if (coachFinanceHome) {
                navigate('home');
                return;
              }
              setOpenServicePanel('training');
              setCurrentView('servicePanel');
            }}
          />
        ) : renderHome();
      case 'communication':
        return hasAnyRole([
          'super_admin', 'admin_d28d', 'admin_gym', 'admin_gym_virtual', 'admin_gimnasio',
          'admin_marca', 'admin_sede', 'entrenador', 'admin_training', 'admin_entrenador', 'nutricionista',
        ]) ? (
          <CommunicationCenterAdmin
            onBack={() => {
              if (inTenantGymPanel) {
                navigate('virtual-gym-panel');
                return;
              }
              if (isSedeAdmin || isGymOnlyAdmin) {
                navigate('home');
                return;
              }
              if (isPureCoach) {
                navigate('home');
                return;
              }
              navigate('masters');
            }}
          />
        ) : renderHome();
      case 'faq-center':
        return hasAnyRole(['super_admin', 'admin_d28d', 'admin_training', 'admin_entrenador']) ? (
          <FaqCenterAdmin />
        ) : null;
      case 'paymentlinks':
        return hasAnyRole(['super_admin']) ? (
          <AdminPaymentLinks onBack={() => navigate('masters')} />
        ) : null;
      case 'modulevigencias':
        return hasAnyRole([
          'super_admin', 'admin_d28d', 'admin_training', 'admin_entrenador',
          'admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_sede', 'entrenador', 'nutricionista',
        ]) ? (
          <AdminModuleVigencias
            scopeIsSede={isSedeAdmin}
            initialModule={
              openServicePanel === 'food-plan'
                ? 'food'
                : (openServicePanel === 'training' ? 'training' : 'food')
            }
            onBack={() => {
              if (openServicePanel === 'd28d') {
                setCurrentView('servicePanel');
                return;
              }
              if (openServicePanel === 'food-plan' || openServicePanel === 'training') {
                setCurrentView('servicePanel');
                return;
              }
              navigate(isSedeAdmin ? 'home' : 'masters');
            }}
          />
        ) : null;
      case 'training-pending':
        return hasAnyRole(['super_admin', 'admin_training', 'admin_d28d']) ? (
          <AdminTrainingPendingPanel />
        ) : renderHome();
      case 'training-coach-config':
        return hasCoachTrainingAccess ? (
          <TrainingCoachClientConfig onBack={() => navigate('home')} />
        ) : renderHome();
      case 'training-coach-vigencias':
        return hasAnyRole(['entrenador', 'admin_training', 'admin_entrenador', 'nutricionista']) ? (
          <TrainingCoachVigenciasPanel
            onBack={() => navigate('home')}
            onOpenActivateClients={() => navigate('training-coach-pay')}
          />
        ) : renderHome();
      case 'training-coach-pay':
        return hasAnyRole(['entrenador', 'admin_training', 'admin_entrenador']) ? (
          <TrainingCoachPaymentPanel onBack={() => navigate('home')} />
        ) : renderHome();
      case 'appearance':
        return hasAnyRole(['super_admin', 'admin_d28d']) ? (
          <AdminFrontendAppearance onBack={() => {
            if (hasAnyRole(['admin_d28d']) && !hasAnyRole(['super_admin'])) {
              navigate('configurations');
              return;
            }
            navigate('masters');
          }}
          />
        ) : null;
      case 'platform-roles':
        return hasAnyRole(['super_admin']) ? (
          <PlatformRolesCatalog onBack={() => navigate('masters')} />
        ) : null;
      case 'spiritual-center':
        return hasAnyRole(['super_admin']) ? (
          <SpiritualCenterShell
            onBack={() => {
              setOpenServicePanel(null);
              setCurrentView('masters');
            }}
          />
        ) : null;
      case 'spiritual-today':
        return canShowSpiritualForUser(user) ? (
          <SpiritualHubView
            onBack={() => navigate('home')}
            onOpenBible={() => navigate('spiritual-bible')}
          />
        ) : renderHome();
      case 'spiritual-bible':
        return (
          <BibleReaderPanel
            onBack={() => navigate(canShowSpiritualForUser(user) ? 'spiritual-today' : 'home')}
          />
        );
      case 'training-gallery': {
        const coachGalleryAccess = hasCoachTrainingAccess;
        const coachClientGallery = isFinal && Boolean(getUserTrainerId(user));
        if (!isFinal && !coachGalleryAccess) return renderHome();
        const canContentGallery = coachGalleryAccess
          || coachClientGallery
          || services.some((s) => s.id === 'training')
          || services.some((s) => s.id === 'd28d' || s.id === 'live-classes');
        if (!canContentGallery) return renderHome();
        return (
          <div className="space-y-4">
            <button type="button" className="btn-secondary text-sm" onClick={() => navigate('home')}>
              {t('common.back_home', 'Volver al inicio')}
            </button>
            <TrainingModule initialSubView="gallery" />
          </div>
        );
      }
      case 'mi-progreso':
        if (isFinal) {
          return (
            <MiProgreso
              initialTab={miProgresoTab}
              onTabChange={setMiProgresoTab}
              onOpenAccount={() => navigate('myaccount')}
              onNavigate={navigate}
              brandName={brandName}
              coachWhiteLabel={coachWhiteLabelActive}
              initialTrainingSubview={null}
              gymBrand={gymBrand}
            />
          );
        }
        if (hasCoachTrainingAccess) {
          return (
            <TrainingExpertProgress
              onBack={openServicePanel === 'training' || isTrainingOps ? backToTrainingPanel : () => navigate('home')}
              onNavigate={navigate}
            />
          );
        }
        return renderHome();
      case 'progress-training':
        if (!hasCoachTrainingAccess) return renderHome();
        return (
          <TrainingExpertProgress
            onBack={openServicePanel === 'training' || isTrainingOps ? backToTrainingPanel : () => navigate('home')}
            onNavigate={navigate}
          />
        );
      case 'progress-food':
        if (coachUsesFoodTrainerPanel) {
          return (
            <div className="dashboard-main-view training-coach-view space-y-4">
              <header className="dashboard-header page-header live-classes-panel__header mb-4">
                <button
                  type="button"
                  className="btn-secondary mb-2"
                  onClick={() => navigate('home')}
                >
                  {t('live.back', '← Volver')}
                </button>
                <h1 className="d28d-page-title">{t('nav.tracking_food', 'Seguimiento food')}</h1>
                <p className="d28d-text-muted subtitle">{t('coach.food_panel_subtitle', 'Panel profesional FOOD PLAN — seguimiento de tus asesorados.')}</p>
              </header>
              <FoodCoachTrackingPanel />
            </div>
          );
        }
        if (isFoodExternal()) {
          openFoodModule('/dashboard');
          return renderHome();
        }
        return <Progress />;
      case 'progress':
        if (openServicePanel === 'training' || (hasCoachTrainingAccess && openServicePanel !== 'food-plan')) {
          return (
            <TrainingExpertProgress
              onBack={openServicePanel === 'training' || isTrainingOps ? backToTrainingPanel : () => navigate('home')}
              onNavigate={navigate}
            />
          );
        }
        if (openServicePanel === 'food-plan' || isFoodTrackingAdmin) {
          return <Progress />;
        }
        if (isFinal) {
          return (
            <MiProgreso
              initialTab={miProgresoTab}
              onTabChange={setMiProgresoTab}
              onOpenAccount={() => navigate('myaccount')}
              onNavigate={navigate}
              brandName={brandName}
              coachWhiteLabel={coachWhiteLabelActive}
              initialTrainingSubview={null}
              gymBrand={gymBrand}
            />
          );
        }
        return <Progress />;
      case 'training-stats':
        if (!hasCoachTrainingAccess) return renderHome();
        return (
          <TrainingExpertProgress
            initialTab="resumen"
            analyticsOnly
            onBack={openServicePanel === 'training' || isTrainingOps ? backToTrainingPanel : () => navigate('home')}
            onNavigate={navigate}
          />
        );
      case 'support':
        return <SupportCenter />;
      case 'coachai':
        if (!hasCoachTrainingAccess) return renderHome();
        return (
          <CoachRoutineAssistant
            onBack={openServicePanel === 'training' || isTrainingOps ? backToTrainingPanel : () => navigate('home')}
          />
        );
      case 'admintrainers':
        if (!hasAnyRole(['admin_training', 'admin_entrenador', 'super_admin'])) return renderHome();
        return (
          <CoachTrainersAdmin
            onBack={isTrainingOps || openServicePanel === 'training' ? backToTrainingPanel : undefined}
          />
        );
      case 'd28d-trainers':
        if (!hasAnyRole(['super_admin', 'admin_d28d', 'admin_marca', 'admin_gimnasio', 'admin_gym'])) {
          return renderHome();
        }
        if (isSedeAdmin && !inTenantGymPanel) return renderHome();
        return (
          <D28dTrainersAdmin
            hasAnyRole={hasAnyRole}
            gymScoped={isGymOnlyAdmin || inTenantGymPanel}
            scopeGymId={(isGymOnlyAdmin || inTenantGymPanel) ? scopedGymId : null}
            scopeGymName={tenantPanelGym?.name || ''}
            onBack={() => {
              if (inTenantGymPanel) {
                navigate('virtual-gym-panel');
                return;
              }
              if (isGymOnlyAdmin) {
                navigate(isSedeAdmin ? 'home' : 'masters');
                return;
              }
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            }}
          />
        );
      case 'd28d-host-daily':
        if (!hasAnyRole(['entrenador_d28d', 'entrenador_gym'])) return renderHome();
        return <D28dHostDailyPanel onBack={() => navigate('home')} />;
      case 'd28d-cycle-reports':
        if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
        return (
          <D28dCycleReportsAdmin
            scopeGymId={(isGymOnlyAdmin || inTenantGymPanel) ? scopedGymId : null}
            onBack={() => {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            }}
          />
        );
      case 'edith-d28d':
        if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
        return (
          <EdithD28dAssistant
            onBack={() => {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            }}
            onNavigate={(view) => navigate(view)}
          />
        );
      case 'd28dtracking':
        if (isHoldingMarca) return renderHome();
        if (!hasAnyRole(['admin_gym_virtual', 'admin_gimnasio', 'admin_gym', 'admin_sede', 'admin_d28d', 'super_admin'])) {
          return renderHome();
        }
        if (isFitLiveUiEnabled() && hasAnyRole(['super_admin'])) {
          return <FitLiveTrackingPanel onBack={() => navigate('home')} />;
        }
        return <D28dCoachTracking onBack={() => navigate('home')} />;
      case 'd28d-challenges-admin':
        if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
        return <D28dChallengesAdmin onBack={() => { setOpenServicePanel('d28d'); setCurrentView('servicePanel'); }} />;
      case 'd28d-challenges':
      case 'd28d-progress':
        return isFinal ? (
          <MiProgreso
            initialTab={miProgresoTab}
            onTabChange={setMiProgresoTab}
            brandName={brandName}
            coachWhiteLabel={coachWhiteLabelActive}
            gymBrand={gymBrand}
          />
        ) : <D28dProgressDashboard />;
      case 'equivalentes': return <Equivalentes />;
      case 'training':
        if (!isFinal && hasCoachTrainingAccess) return renderHome();
        return <TrainingModule />;
      case 'admintraining':
        if (!hasAnyRole(['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador', 'super_admin', 'admin_marca', 'admin_gimnasio'])) {
          return renderHome();
        }
        return <AdminTrainingManager onBack={backToTrainingPanel} onNavigate={navigate} />;
      case 'admingallery': {
        if (!hasCoachTrainingAccess || !isTrainingGalleryContext) return renderHome();
        if (openServicePanel === 'd28d') return renderHome();
        return <AdminTrainingGallery />;
      }
      case 'd28d-gallery': {
        const canViewConsumerGallery = isFinal && !accessBlocked && (
          (serviceShellUx.hasD28d && !isSedeFinal) || isSedeFinal
        );
        if (!canManageD28dClassGallery && !canViewConsumerGallery) return renderHome();
        const backD28d = canManageD28dClassGallery
          ? (inTenantGymPanel
            ? () => navigate('virtual-gym-panel')
            : (isSedeAdmin
              ? () => navigate('home')
              : (isGymOnlyAdmin
                ? () => navigate('masters')
                : (openServicePanel === 'd28d'
                ? () => { setOpenServicePanel('d28d'); setCurrentView('servicePanel'); }
                : () => navigate('home')))))
          : () => navigate('home');
        return (
          <AdminD28dClassGallery
            onBack={backD28d}
            readOnly={isSedeAdmin || (canViewConsumerGallery && !canManageD28dClassGallery)}
            scopeGymId={(isGymOnlyAdmin || isSedeAdmin || inTenantGymPanel) ? scopedGymId : null}
            scopeGymName={tenantPanelGym?.name || ''}
          />
        );
      }
      case 'gym-attendance':
        if (!isGymOnlyAdmin && !inTenantGymPanel) return renderHome();
        return (
          <GymAttendancePanel
            scopeGymId={scopedGymId}
            scopeGymName={tenantPanelGym?.name || ''}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : 'home')}
          />
        );
      case 'liveclasses':
        return (
          <LiveClassesPanel
            user={user}
            canProgram={false}
            consumerOnly
            programId={selectedProgram}
            scopeGymId={(isGymOnlyAdmin || isSedeAdmin || inTenantGymPanel) ? scopedGymId : null}
            scopeGymName={tenantPanelGym?.name || ''}
            onBack={isSedeAdmin ? () => navigate('home') : undefined}
          />
        );
      case 'adminliveclasses': {
        const canProgramLive = hasAnyRole(['super_admin', 'admin_gym_virtual', 'admin_marca', 'admin_gimnasio', 'admin_gym', 'admin_d28d']);
        if (!canProgramLive || (isSedeAdmin && !inTenantGymPanel)) return renderHome();
        return (
          <LiveClassesPanel
            user={user}
            canProgram
            programId={selectedProgram}
            defaultTab="program"
            scopeGymId={(isGymOnlyAdmin || inTenantGymPanel) ? scopedGymId : null}
            scopeGymName={tenantPanelGym?.name || ''}
            onBack={(isGymOnlyAdmin || inTenantGymPanel)
              ? () => navigate(inTenantGymPanel ? 'virtual-gym-panel' : (isSedeAdmin ? 'home' : 'masters'))
              : undefined}
          />
        );
      }
      case 'programs':
        if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
        return (
          <AdminProgramsManager
            initialProgramId={selectedProgram}
            onBack={hasAnyRole(['super_admin']) ? () => {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            } : undefined}
          />
        );
      case 'commercial-plans-coach':
        if (!isNicolasAnchorCoach) return renderHome();
        return (
          <CommercialPlansHub
            d28dOnly={false}
            trainingOnly
            onBack={backToTrainingPanel}
          />
        );
      case 'commercial-plans':
        if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
        return (
          <CommercialPlansHub
            d28dOnly={hasAnyRole(['admin_d28d']) && !hasAnyRole(['super_admin'])}
            onBack={() => {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            }}
            onOpenVigencias={() => navigate('modulevigencias')}
          />
        );
      case 'configurations':
        if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
        return (
          <ConfigurationsHub
            hasAnyRole={hasAnyRole}
            onBack={() => {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            }}
            onNavigate={(view) => navigate(view)}
          />
        );
      case 'd28dzoom':
        if (!hasAnyRole(['super_admin', 'admin_d28d'])) return renderHome();
        return (
          <D28dZoomAccountsMaster
            onBack={() => {
              setOpenServicePanel('d28d');
              setCurrentView('servicePanel');
            }}
          />
        );
      case 'virtual-gym-panel':
        if (!hasAnyRole(['super_admin']) || !tenantPanelGym?.id) return renderHome();
        return (
          <GymMastersHub
            scopeGymId={tenantPanelGym.id}
            scopeGymName={tenantPanelGym.name}
            onBack={() => {
              setTenantPanelGym(null);
              navigate('organizations');
            }}
            onNavigate={navigate}
          />
        );
      case 'masters':
        if (isHoldingMarca) return renderHome();
        if (isSedeAdmin) return renderHome();
        if (isGymOnlyAdmin) {
          return <GymMastersHub onNavigate={navigate} />;
        }
        return (
          <MastersHub
            hasAnyRole={hasAnyRole}
            onOpenMaster={(moduleId) => {
              const id = LEGACY_MASTER_REDIRECTS[moduleId] || moduleId;
              if (id === 'spiritual-center') {
                setOpenServicePanel(null);
                setCurrentView('spiritual-center');
                return;
              }
              if (id === 'modulevigencias') {
                setOpenServicePanel(null);
                setCurrentView('modulevigencias');
                return;
              }
              setOpenServicePanel(id);
              setCurrentView('servicePanel');
            }}
          />
        );
      case 'gym-masters-plans':
        if (!inTenantGymPanel && (isSedeAdmin || !isGymOnlyAdmin)) return renderHome();
        return (
          <GymMastersPlans
            scopeGymId={scopedGymId}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : 'masters')}
          />
        );
      case 'gym-masters-cycles':
        if (!inTenantGymPanel && (isSedeAdmin || !isGymOnlyAdmin)) return renderHome();
        return (
          <GymMastersCycles
            scopeGymId={scopedGymId}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : 'masters')}
          />
        );
      case 'gym-masters-programs':
        if (!inTenantGymPanel && (isSedeAdmin || !isGymOnlyAdmin)) return renderHome();
        return (
          <GymMastersPrograms
            scopeGymId={scopedGymId}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : 'masters')}
          />
        );
      case 'gym-masters-routines':
        if (!inTenantGymPanel && (isSedeAdmin || !isGymOnlyAdmin)) return renderHome();
        return (
          <GymMastersRoutines
            scopeGymId={scopedGymId}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : 'masters')}
          />
        );
      case 'gym-masters-trainers':
        if (!inTenantGymPanel && (isSedeAdmin || !isGymOnlyAdmin)) return renderHome();
        return (
          <GymMastersTrainers
            scopeGymId={scopedGymId}
            scopeGymName={tenantPanelGym?.name || ''}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : 'masters')}
          />
        );
      case 'gym-masters-challenges':
        if (!inTenantGymPanel && (isSedeAdmin || !isGymOnlyAdmin)) return renderHome();
        return (
          <GymMastersChallenges
            scopeGymId={scopedGymId}
            onBack={() => navigate(inTenantGymPanel ? 'virtual-gym-panel' : 'masters')}
          />
        );
      case 'myplan':
        return (
          <MyPlanView
            myPlan={myPlan}
            planLoading={planLoading}
            dayTotals={dayTotals}
            onNavigate={navigate}
          />
        );
      case 'servicePanel': return renderServicePanel();
      case 'home':
      default:
        return renderHome();
    }
  };

  return (
    <div className={`dashboard-container${coachWhiteLabelActive ? ' coach-white-label' : ''}${trainingConsumerChrome ? ' training-consumer-view' : ''}`}>
      <nav className="navbar">
        <div className="navbar-brand">
          {navbarBrand.showLogo ? (
            <img
              src={brandLogoSrc}
              alt={navbarBrand.fullName}
              className="navbar-brand-logo"
              onError={() => setBrandLogoFailed(true)}
            />
          ) : null}
          {navbarBrand.showText ? (
            <h1 className="navbar-brand-title">
              {coachWhiteLabelActive ? (
                <span className="brand-accent">{navbarBrand.fullName}</span>
              ) : navbarBrand.textOnlyTail ? (
                <span className="brand-accent">{navbarBrand.tail}</span>
              ) : (
                <>
                  <span className="brand-d28d">{navbarBrand.head}</span>
                  {navbarBrand.tail ? ` ${navbarBrand.tail}` : ''}
                </>
              )}
            </h1>
          ) : null}
        </div>
        <div className="navbar-menu">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.id)}
              className={
                currentView === item.id
                || (item.id === 'servicePanel' && currentView === 'servicePanel')
                || (item.id === 'mi-progreso' && ['d28d-challenges', 'd28d-progress'].includes(currentView))
                || (item.id === 'progress' && ['progress', 'progress-training'].includes(currentView))
                || (item.id === 'progress-training' && currentView === 'progress-training')
                || (item.id === 'progress-food' && currentView === 'progress-food')
                || (item.id === 'spiritual-today' && ['spiritual-today', 'spiritual-bible'].includes(currentView))
                  ? 'nav-link active'
                  : 'nav-link'
              }
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="navbar-user">
          <ShellNotificationBell
            isAdmin={hasAnyRole([
              'super_admin', 'admin_d28d', 'admin_training', 'admin_entrenador',
              'admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_gym_virtual', 'admin_sede',
              'entrenador', 'nutricionista',
            ])}
            onAdminNavigate={(action) => {
              if (!action?.view) return;
              if (action.openActivate && action.userId != null) {
                setPendingActivateUserId(Number(action.userId));
              } else {
                setPendingActivateUserId(null);
              }
              navigate(action.view);
            }}
          />
          <div className="mr-3">
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="input py-1 text-sm"
              aria-label={t('lang.label', 'Idioma')}
            >
              <option value="es">{t('lang.es', 'Español')}</option>
              <option value="en">{t('lang.en', 'Inglés')}</option>
            </select>
          </div>
          <span className="user-name">{user?.nombre}</span>
          {hasAnyRole(['super_admin', 'admin_d28d']) ? (
            <BuildStamp className="hidden sm:block mr-2" />
          ) : null}
          <button onClick={logout} className="btn-logout">{t('auth.logout', 'Cerrar Sesión')}</button>
        </div>
      </nav>

      <div className="dashboard-content">
        {renderContent()}
      </div>

      {isFinal && !isSedeFinal && !accessBlocked && serviceShellUx.hasD28d ? (
        <D28dTrainerEvalGate enabled />
      ) : null}

      {/* Asistente nutricional legacy (shell). Oculto si FOOD-only usa módulo externo. */}
      {isFinal && serviceShellUx.showNutritionChatFab && (
        <div className="fixed bottom-4 right-4 z-40">
          {!chatOpen && (
            <button
              className="fab-assistant"
              onClick={() => setChatOpen(true)}
              aria-label={t('nav.assistant', 'Asistente')}
            >
              {t('nav.assistant', 'Asistente')}
            </button>
          )}
        </div>
      )}
      {chatOpen && isFinal && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/50 border-0 p-0 cursor-default"
            aria-label={t('common.close', 'Cerrar')}
            onClick={() => setChatOpen(false)}
          />
          <NutritionChat embedded onRequestClose={() => setChatOpen(false)} />
        </>
      )}
    </div>
  );
}
